import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
let auth0TokenGetter = null;


const expiryTime = 3600 * 1000; // 1 hour in milliseconds

const isCacheValid = (cacheTime, expiryTime) => {
  return cacheTime && (Date.now() - cacheTime < expiryTime);
};

const apiHelper = {
  setAuth0TokenGetter: (tokenGetter) => {
    if (typeof tokenGetter !== 'function') {
      throw new Error('Invalid token getter provided');
    }
    auth0TokenGetter = tokenGetter;
  },

  isInitialized: () => {
    return !!auth0TokenGetter;
  },

  initialize: async (tokenGetter) => {
  
    try {
      apiHelper.setAuth0TokenGetter(tokenGetter);
     
      // Test the token getter immediately to ensure it works
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token getter timeout')), 10000) // 10 second timeout
      );
      
      const tokenPromise = apiHelper.getAuthToken();
      
    
      const token = await Promise.race([tokenPromise, timeoutPromise]);


      return !!token;
    } catch (error) {
      console.error('API initialization failed:', error.message);
      // Don't throw the error immediately - allow the app to continue
      // The specific API calls will handle authentication errors individually
      if (error.message.includes('timeout')) {
        console.warn('Token getter timed out, but continuing initialization...');
        return false; // Return false but don't throw
      }
      throw error;
    }
  },

  retryInitialization: async (tokenGetter) => {
    return await apiHelper.initialize(tokenGetter);
  },

  getAuthToken: async () => {
    if (!auth0TokenGetter) {
      throw new Error('Auth0 token getter not initialized');
    }
    try {
      // Add timeout protection for the auth0TokenGetter call
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth token request timeout')), 15000) // 15 second timeout
      );
      
      const tokenPromise = auth0TokenGetter();
      
      return await Promise.race([tokenPromise, timeoutPromise]);
    } catch (error) {
      throw new Error(`Error getting auth token: ${error.message}`);
    }
  },

  handleResponse: async (response) => {
    if (response.status === 401 || response.status === 403) {
      try {
        await apiHelper.getAuthToken();
      } catch (error) {
        throw new Error('Authentication failed - please try again');
      }
    }
    if (!response.ok) {
      const errorMessage = `Error: ${response.status} - ${response.statusText}`;
      throw new Error(errorMessage);
    }
    return response;
  },

  fetchJSONData: async (endpoint, options = {}) => {
    if (!auth0TokenGetter) {
      throw new Error('API not initialized. Please ensure initializeAPI is called first.');
    }

    try {
      const headers = await apiHelper.getHeaders();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          try {
            const newHeaders = await apiHelper.getHeaders(true);
            const retryResponse = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
              ...options,
              headers: {
                ...newHeaders,
                ...options.headers
              }
            });

            if (!retryResponse.ok) {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
            return retryResponse.json();
          } catch (retryError) {
            console.error('Retry with fresh token failed:', retryError);
            throw new Error(`Authentication failed: ${retryError.message}`);
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error in fetchJSONData for ${endpoint}:`, error);
      throw error;
    }
  },

  fetchPDFData: async (endpoint) => {
   
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');

      const fullUrl = `${apiUrl}/api/graph/${endpoint}`;
      

      const response = await axios.get(fullUrl, {
        headers: await apiHelper.getHeaders(),
        responseType: 'blob'  // Important for PDF data
      });

      return response.data;
    } catch (err) {
      console.error("Error in fetchPDFData:", err);
      throw err;
    }
  },

  fetchJSONFileByNameAndContent: async function (fileName, bypassCache = false) {
    const cacheKey = `jsonFile-${fileName}`;
    const cacheExpiryKey = `${cacheKey}-timestamp`;
    let fileId;

    if (!bypassCache) {
      const cachedData = await this.fetchJSONData(`graph/getFileIdByFileName/${fileName}`);
      const cacheTime = await this.fetchJSONData(cacheExpiryKey);

      if (cachedData && isCacheValid(cacheTime, expiryTime)) {
        return { fileContent: cachedData };
      }
      if (cachedData?.fileId) {
        fileId = cachedData.fileId;
      }
    }

    try {
      if (!fileId) {
        const fileData = await this.fetchJSONData(`graph/getFileIdByFileName/${fileName}`);
        fileId = fileData.fileId;
      }

      if (!fileId) {
        throw new Error('File ID not found');
      }

      const fileContent = await this.fetchJSONData(`graph/getJSONContent/${fileId}`);
      await this.fetchJSONData(cacheExpiryKey, Date.now());
      return { fileContent };
    } catch (err) {
      throw err;
    }
  },

  fetchPDFNameAndContentById: async function (listItemId, bypassCache = false) {
    try {
      // Directly fetch the PDF content
      const fileContent = await this.fetchPDFData(`getFileContentByListItemId/${listItemId}`);
      return { fileContent };
    } catch (err) {
      console.error("Error fetching PDF:", err);
      throw err;
    }
  },

  fetchPDFNameAndContentByTitle: async function (title) {
    try {
        // Simply fetch the PDF content by title
        const fileContent = await this.fetchPDFData(`getFileContentByTitle/${title}`);
        return { fileContent };
    } catch (err) {
        throw err;
    }
  },


  fetchNewsData: async function () {
    try {

      const response = await this.get(`${process.env.REACT_APP_API_URL}/api/graph/getAllNews`);

      const fileContent = response.data.news;

      const newsData = {
        newsArticles: Array.isArray(fileContent) ? fileContent : [],
        weeklySections: Array.isArray(fileContent)
          ? [...new Set(fileContent.map(article => article.weeklySection).filter(Boolean))]
          : []
      };


      return newsData;
    } catch (err) {
      console.error('Error in fetchNewsData:', err);
      return { newsArticles: [], weeklySections: [] };
    }
  },

  getHeaders: async (forceNew = false) => {
    const token = await apiHelper.getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  },

  get: async (url, config = {}) => {
    const headers = await apiHelper.getHeaders();
    const finalUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;



    try {
      const response = await axios.get(finalUrl, {
        ...config,
        headers: {
          ...headers,
          ...config.headers
        }
      });
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed - please log in again');
      }
      throw error;
    }
  },

  post: async (endpoint, data, options = {}) => {
    const headers = await apiHelper.getHeaders();
    const finalUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    try {
      const config = { 
        headers,
        ...options // Allow timeout and other config overrides
      };
      const response = await axios.post(finalUrl, data, config);
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed - please log in again');
      }
      throw error;
    }
  },

  put: async (endpoint, data) => {
    const headers = await apiHelper.getHeaders();
    const finalUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    try {
      const response = await axios.put(finalUrl, data, { headers });
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed - please log in again');
      }
      throw error;
    }
  },

  delete: async (endpoint) => {
    const headers = await apiHelper.getHeaders();
    const finalUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    try {
      const response = await axios.delete(finalUrl, { headers });
      return response;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Authentication failed - please log in again');
      }
      throw error;
    }
  },

  generateSocialContent: async function (articleData, platform = 'linkedin') {
    try {
      const response = await this.post('/api/daire/generateSocialContent', {
        title: articleData.title,
        content: articleData.content,
        platform,
        userName: localStorage.getItem('userName') // If you store the user name
      });

      return response;
    } catch (err) {
      console.error('Error generating social content:', err);
      throw err;
    }
  },

  logUserActivity: async function (action, details = '', user = null) {
    try {
      if (!user) {
        console.warn('No user provided for activity logging');
        return;
      }

      const logData = {
        userName: user.name,
        email: user.email,
        action: action,
        details: details
      };

      await this.post('/api/graph/logUserActivity', logData);
    } catch (error) {
      console.error('Error logging user activity:', error);
    }
  },

  updateSocialPost: async function (itemId, socialFields, isSubmittedArticle) {
    try {
      const endpoint = `/api/graph/updateSocialPost/${itemId}`;
      const response = await this.post(endpoint, {
        fields: socialFields,
        isSubmittedArticle
      });
      return response.data;
    } catch (err) {
      console.error('Error updating social post:', err);
      throw err;
    }
  },

  // Qdrant Index
  indexQdrantDocument: async function (text, metadata = {}, documentId) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await this.getAuthToken();
      const response = await fetch(`${apiUrl}/api/qdrant/index`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ text, metadata, documentId })
      });

      await this.handleResponse(response);
      return await response.json();
    } catch (err) {
      console.error('Error indexing document into Qdrant:', err.message);
      throw err;
    }
  },

  deleteQdrantDocument: async function(documentId, type) {
    try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');
        const authToken = await this.getAuthToken();
        const response = await fetch(`${apiUrl}/api/qdrant/delete/${documentId}/${type}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`${response.status} - ${response.statusText}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error deleting document from Qdrant:', err.message);
        throw err;
    }
  },

  // Index Working Group Periodic Updates
  indexWGPeriodicUpdates: async function() {
    try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');
        const authToken = await this.getAuthToken();
        const response = await fetch(`${apiUrl}/api/qdrant/index-wg-updates`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `${response.status} - ${response.statusText}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error indexing WG periodic updates:', err.message);
        throw err;
    }
  },

  // Index News Articles
  indexNewsArticles: async function() {
    try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) throw new Error('API URL is not defined');
        const authToken = await this.getAuthToken();
        const response = await fetch(`${apiUrl}/api/qdrant/index-news`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `${response.status} - ${response.statusText}`);
        }

        return await response.json();
    } catch (err) {
        console.error('Error indexing news articles:', err.message);
        throw err;
    }
  },

  // Qdrant Indexing Status
  getQdrantIndexingStatus: async function(documentId, type) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) throw new Error('API URL is not defined');
      const authToken = await apiHelper.getAuthToken();
      const response = await fetch(`${apiUrl}/api/qdrant/indexing-status/${documentId}/${type}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      await apiHelper.handleResponse(response);
      const jsonResponse = await response.json();
      return { data: jsonResponse };  // Wrap the response in a data property
    } catch (err) {
      console.error('Error getting document indexing status:', err.message);
      throw err;
    }
  },

  // Daire Usage Tracking
  getDaireUsage: async function () {
    try {
      const response = await this.get('/api/daire/usage');
      return response.data;
    } catch (error) {
      console.error('Error fetching Daire usage:', error);
      throw error;
    }
  },

  getDaireUsageSummary: async function () {
    try {
      const response = await this.get('/api/daire/usage/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching Daire usage summary:', error);
      throw error;
    }
  },

  trackDaireUsage: async function (usageData) {
    try {
      const response = await this.post('/api/daire/track-usage', usageData);
      return response.data;
    } catch (error) {
      console.error('Error tracking Daire usage:', error);
      throw error;
    }
  },

  // Basic HTTP methods
  get: async function (url) {
    const response = await axiosInstance.get(url);
    return response;
  },

  post: async function (url, data) {
    const response = await axiosInstance.post(url, data);
    return response;
  },

  put: async function (url, data) {
    const response = await axiosInstance.put(url, data);
    return response;
  },

  delete: async function (url) {
    const response = await axiosInstance.delete(url);
    return response;
  },

  // User Activity Tracking
  getUserActivity: async function (filter = 'all', days = 1, user = 'all') {
    try {
      const params = new URLSearchParams({ filter, days, user });
      const response = await this.get(`/api/graph/getUserActivity?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user activity:', error);
      throw error;
    }
  },

  getActivityUsers: async function () {
    try {
      const response = await this.get('/api/graph/getActivityUsers');
      return response.data;
    } catch (error) {
      console.error('Error fetching activity users:', error);
      throw error;
    }
  },

  getMemberProfileByEmail: async function (email) {
    try {
      const response = await this.get(`/api/graph/getMemberProfileByEmail/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching member profile by email:', error);
      throw error;
    }
  },

};

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // Default timeout for most requests
  validateStatus: status => status >= 200 && status < 300
});

// Add request interceptor to always get fresh headers
axiosInstance.interceptors.request.use(
  async config => {
    const headers = await apiHelper.getHeaders();
    config.headers = { ...config.headers, ...headers };
    return config;
  },
  error => Promise.reject(error)
);

// Update response interceptor for better error handling
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 401 || error.response?.status === 403) {
     
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        const headers = await apiHelper.getHeaders();
        originalRequest.headers = { ...originalRequest.headers, ...headers };
        return axiosInstance(originalRequest);
      }
    }
    throw error;
  }
);

export const initializeAPI = apiHelper.initialize;
export const retryInitializeAPI = apiHelper.retryInitialization;
export const isAPIInitialized = apiHelper.isInitialized;
export default apiHelper;
